"use client"

import { useEffect, useState } from "react"
import { PricingPlan } from "@/app/types/PricingPlan"
import { Subscription } from "@/app/types/Subscription"
import type { Plan, StarterPlan, ProPlan, AdvancedPlan, CustomPlan, PricingMode } from "./types"
import { STARTER_FEATURES, PRO_FEATURES, ADVANCED_FEATURES } from "./constants"
import type { PaygPricingItem } from "./types"
import { normalizePaygAmountCents } from "@/lib/payg-pricing"

export interface UsePricingTableProps {
    currentSubscription?: Subscription
    publicMode?: boolean
    changePlan?: (plan: PricingPlan) => void
    onPlanUpdate?: (planType: 'starter' | 'pro' | 'advanced', isYearly: boolean) => void
    initialPlanType?: 'starter' | 'pro' | 'advanced'
    initialIsYearly?: boolean
    initialMode?: PricingMode
    initialPaygAmountCents?: number
    initialAutoRenew?: boolean
    paygPricing?: PaygPricingItem | null
    onPaygCheckout?: (amountCents: number, autoRenew: boolean) => void
    onModeChange?: (mode: PricingMode) => void
}

export function usePricingTable({
    currentSubscription,
    publicMode,
    changePlan,
    onPlanUpdate,
    initialPlanType,
    initialMode,
    initialPaygAmountCents,
    initialAutoRenew,
    onPaygCheckout,
    onModeChange,
    paygPricing,
}: UsePricingTableProps) {
    const [mode, setMode] = useState<PricingMode>(initialMode ?? 'payg')
    const isYearly = mode !== 'monthly'
    const [paygAmountCents, setPaygAmountCents] = useState(initialPaygAmountCents ?? 0)
    const [autoRenew, setAutoRenew] = useState(initialAutoRenew ?? false)
    const [isLoadingInner, setIsLoadingInner] = useState(false)
    const [loadingPlan, setLoadingPlan] = useState<string | null>(null)

    useEffect(() => {
        const normalized = normalizePaygAmountCents(initialPaygAmountCents, paygPricing)
        if (normalized !== null) setPaygAmountCents(normalized)
    }, [initialPaygAmountCents, paygPricing])

    useEffect(() => {
        setMode(initialMode ?? 'payg')
    }, [initialMode])

    useEffect(() => {
        setAutoRenew(initialAutoRenew ?? false)
    }, [initialAutoRenew])

    const getActionLabel = (planType: 'starter' | 'pro' | 'advanced') => {
        if (!currentSubscription) return "Choose Plan"
        // While on a free trial the user has no paid plan yet, so any paid plan
        // is an upgrade (not a downgrade), regardless of the trial's underlying tier.
        if (currentSubscription.status === 'trialing') return "Upgrade"
        const planHierarchy = { starter: 1, pro: 2, advanced: 3 }
        const currentLevel = planHierarchy[currentSubscription.planType] || 0
        const targetLevel = planHierarchy[planType]

        if (targetLevel > currentLevel) return "Upgrade"
        if (targetLevel < currentLevel) return "Downgrade"
        return "Current Plan"
    }

    const toggleYearly = (value: string) => {
        const newMode = value as PricingMode
        setMode(newMode)
        if (newMode === 'payg') {
            onModeChange?.(newMode)
        } else {
            onPlanUpdate?.(
                initialPlanType ?? currentSubscription?.planType ?? 'starter',
                newMode === 'annual',
            )
        }
    }

    const handlePlanSelect = async (
        plan: Omit<StarterPlan, 'isCurrent'> | Omit<CustomPlan, 'isCurrent'> | Omit<ProPlan, 'isCurrent'> | Omit<AdvancedPlan, 'isCurrent'>
    ) => {
        if (publicMode) {
            const params = new URLSearchParams()
            params.set('planId', plan.title)
            params.set('planType', plan.type)
            params.set('billingMode', isYearly ? 'annual' : 'monthly')
            params.set('isYearly', isYearly.toString())
            const redirectUrl = '/login?redirect_to=' + encodeURIComponent('/earn-credits/billing' + (params.toString() ? '?' + params.toString() : ''))
            window.open('https://app.clearitty.com/signup?redirect_to=' + encodeURIComponent(redirectUrl), '_blank')
            return
        }
        setLoadingPlan(plan.title)
        if (plan.type === "custom") {
            window.open('https://www.clearitty.com/contact', '_blank')
        } else {
            setIsLoadingInner(true)
            if (changePlan) {
                changePlan({
                    planId: plan.title,
                    planType: plan.type,
                    isYearly,
                })
            }
            setIsLoadingInner(false)
        }
        setLoadingPlan(null)
    }

    const handlePaygCheckout = () => {
        onPaygCheckout?.(paygAmountCents, autoRenew)
    }

    const defaultPlans: Plan[] = [
        {
            type: 'starter',
            title: "Starter",
            description: "Perfect for individual sellers",
            maxSeats: 3,
            features: STARTER_FEATURES,
            actionLabel: getActionLabel('starter'),
            isCurrent: false,
        },
        {
            type: 'pro',
            title: "Pro",
            description: "For growing sales teams",
            maxSeats: 3,
            hasTrial: true,
            features: PRO_FEATURES,
            actionLabel: getActionLabel('pro'),
            isCurrent: false,
        },
        {
            type: 'advanced',
            title: "Advanced",
            description: "Full power for enterprise teams",
            maxSeats: 6,
            features: ADVANCED_FEATURES,
            actionLabel: getActionLabel('advanced'),
            isCurrent: false,
        },
    ]

    return {
        mode,
        isYearly,
        paygAmountCents,
        setPaygAmountCents,
        autoRenew,
        setAutoRenew,
        isLoadingInner,
        loadingPlan,
        toggleYearly,
        handlePlanSelect,
        handlePaygCheckout,
        defaultPlans,
    }
}
